// ABOUTME: Public privacy policy page accessible via direct URL
// ABOUTME: Displays privacy policy content without authentication requirement

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Avatar
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { renderPolicyMarkdown } from '../utils/sanitize';
import { getPrivacyPolicy } from '../services/api';
import type { PolicyDocument } from '../types/api';
import Footer from './Footer';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<PolicyDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPolicy = async () => {
      try {
        setLoading(true);
        const policyData = await getPrivacyPolicy();
        setPolicy(policyData);
      } catch {
        setError('Failed to load Privacy Policy');
      } finally {
        setLoading(false);
      }
    };

    loadPolicy();
  }, []);

  const renderMarkdown = renderPolicyMarkdown;

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header with Back Button */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            variant="outlined"
            size="small"
          >
            Back
          </Button>
        </Box>

        {/* Branding Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 108,
              height: 108,
              mx: 'auto',
              mb: 2
            }}
          >
            <Box
              component="img"
              src="/lets-reset-logo-108.png"
              alt="Let's Reset logo"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </Avatar>
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            Let&apos;s Reset
          </Typography>
        </Box>

        {/* Content */}
        <Paper elevation={2} sx={{ p: 4 }}>
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {policy && !loading && (
            <Box>
              <div
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(policy.content)
                }}
              />
              
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Last updated: {new Date(policy.lastModified).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Version: {policy.version} {policy.versionLabel && `(${policy.versionLabel})`}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
      
      <Footer variant="login" />
    </>
  );
};

export default PrivacyPolicyPage;